/**
 * One display name per platform, everywhere a reader sees one.
 *
 * There were three of these tables and they disagreed. systemHealth said
 * "Facebook Ads", the MMM correlation grid said "Facebook", and
 * mediaIntelligenceService said "Facebook" — so the same connection was named
 * two different things depending on which panel you were looking at, and the
 * Dashboard's own status strip named it neither, rendering the raw column value
 * in capitals: FACEBOOK, ANALYTICS.
 *
 * The "Ads" suffix is kept rather than trimmed, because for Google it is not
 * decoration: "Google Ads" and "Google Analytics" are two different connections
 * this product holds at once, and they answer different questions — one buys
 * placements, the other measures a website. Naming them both "Google" would
 * undo the separation the Dashboard strip draws on purpose.
 *
 * The single exception is PLATFORM_SHORT_LABEL, below: the Channel
 * Deep-dive's own channel names, wherever that page names a channel (its tabs,
 * box titles, empty state, Ads Manager link, file and chat). GA4 is a channel
 * of its own there, so "Google" cannot be mistaken for Google Analytics.
 *
 * @module utils/platformLabel
 */

/**
 * Keyed by the STORED platform vocabulary — the `media_platform_accounts`
 * enum — plus the two connect-flow spellings.
 *
 * `google_ads` and `google_analytics` name OAuth providers a person picks from,
 * not stored accounts, and migration 080 exists because writing them into
 * sync_jobs split a platform's history into two piles that each looked
 * complete. They are mapped here rather than dropped: a label that silently
 * fell through to the raw id would make that confusion visible to users instead
 * of to us.
 */
export const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Facebook Ads',
  google: 'Google Ads',
  google_ads: 'Google Ads',
  tiktok: 'TikTok Ads',
  line: 'LINE Ads',
  analytics: 'Google Analytics',
  google_analytics: 'Google Analytics',
};

/**
 * The reader-facing name for a platform, falling back to the raw identifier.
 *
 * Never blank and never a throw: an unrecognised platform is far more likely to
 * be one newly connected than a bug, and showing `pinterest` is honest where an
 * empty cell would read as a platform with no name.
 */
export const platformLabel = (platform: string): string =>
  PLATFORM_LABEL[platform] ?? platform;

/**
 * The Channel Deep-dive's channel names (spec §4), first drawn on its tabs:
 * Facebook · TikTok · Google · LINE Ads, then GA4. A tab row has no room for the
 * "Ads" suffix, and "Google" is not ambiguous on that page, because GA4 is a
 * channel of its own. They live in this file so there is still one file of
 * platform names (R33).
 */
export const PLATFORM_SHORT_LABEL: Readonly<Record<string, string>> = {
  facebook: 'Facebook',
  tiktok: 'TikTok',
  google: 'Google',
  line: 'LINE Ads',
  analytics: 'GA4',
};

/** A channel's short name on the Deep-dive; any other platform falls back to platformLabel. */
export const platformShortLabel = (platform: string): string =>
  PLATFORM_SHORT_LABEL[platform] ?? platformLabel(platform);

export default platformLabel;
